import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { ArrowLeft, Eye, EyeOff, LockKeyhole, Mail, ShieldCheck } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { authApi, useDevelopmentFixtures } from "../api/client";
import { Brand } from "../components/Brand";
import { Button, Field, InlineNotice } from "../components/ui";
import { useAuth } from "./AuthProvider";

function AuthLayout({ children, title, description }: { children: ReactNode; title: string; description: string }) {
  return (
    <main className="auth-layout">
      <section className="auth-brand-panel" aria-label="Ruta Mágica">
        <Brand inverse />
        <div className="auth-brand-panel__message">
          <ShieldCheck aria-hidden="true" />
          <h2>Operación segura, decisiones claras.</h2>
          <p>Pedidos, reservas y confirmaciones en un solo lugar.</p>
        </div>
        <small>Acceso exclusivo para el equipo autorizado.</small>
      </section>
      <section className="auth-form-panel">
        <div className="auth-mobile-brand"><Brand /></div>
        <div className="auth-card">
          <header>
            <h1>{title}</h1>
            <p>{description}</p>
          </header>
          {children}
        </div>
      </section>
    </main>
  );
}

function PasswordField({ value, onChange, label = "Contraseña", autoComplete = "current-password" }: {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  autoComplete?: string;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="password-field">
      <Field
        label={label}
        type={visible ? "text" : "password"}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        autoComplete={autoComplete}
        prefixIcon={LockKeyhole}
        required
        minLength={12}
      />
      <button type="button" onClick={() => setVisible((current) => !current)} aria-label={visible ? "Ocultar contraseña" : "Mostrar contraseña"}>
        {visible ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  );
}

function useSensitiveActionToken(): string {
  const location = useLocation();
  const navigate = useNavigate();
  const [token] = useState(() => new URLSearchParams(location.search).get("token") ?? "");

  useEffect(() => {
    const parameters = new URLSearchParams(location.search);
    if (!parameters.has("token")) return;
    parameters.delete("token");
    const search = parameters.toString();
    navigate({
      pathname: location.pathname,
      search: search ? `?${search}` : "",
      hash: location.hash,
    }, { replace: true });
  }, [location.hash, location.pathname, location.search, navigate]);

  return token;
}

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim(), password, rememberDevice);
      const from = (location.state as { from?: { pathname?: string; search?: string } } | null)?.from;
      navigate(from?.pathname?.startsWith("/") && !from.pathname.startsWith("//") ? `${from.pathname}${from.search ?? ""}` : "/", { replace: true });
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "No fue posible iniciar sesión.";
      setError(/correo.*contrase(?:ñ|n)a.*c[oó]digo|datos.*int[eé]ntalo/i.test(message) ? "Correo o contraseña incorrectos." : message);
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <AuthLayout title="Iniciar sesión" description="Entra con tu correo y contraseña.">
      <form onSubmit={submit} className="auth-form" noValidate>
        {error ? <InlineNotice tone="danger">{error}</InlineNotice> : null}
        <Field label="Correo de trabajo" type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="username" prefixIcon={Mail} required />
        <PasswordField value={password} onChange={setPassword} />
        <label className="auth-remember-device">
          <input
            type="checkbox"
            checked={rememberDevice}
            onChange={(event) => setRememberDevice(event.target.checked)}
          />
          <span>
            <strong>Mantener sesión en este dispositivo</strong>
            <small>Con uso frecuente, hasta 30 días. Se cerrará después de 7 días sin actividad.</small>
          </span>
        </label>
        <div className="auth-form__meta auth-form__meta--end"><Link to="/recuperar">¿Olvidaste tu contraseña?</Link></div>
        <Button type="submit" disabled={submitting} className="button--full">
          {submitting ? "Verificando..." : "Entrar al panel"}
        </Button>
        <p className="auth-session-note"><ShieldCheck size={16} aria-hidden="true" /> Usa un dispositivo de confianza y cierra sesión al terminar.</p>
        {useDevelopmentFixtures ? <p className="dev-fixture-note">Modo local: usa cualquier correo y una contraseña de 12 caracteres.</p> : null}
      </form>
    </AuthLayout>
  );
}

export function RecoveryPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      if (!useDevelopmentFixtures) await authApi.recover(email.trim());
      else await new Promise((resolve) => window.setTimeout(resolve, 350));
      setSent(true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "No fue posible solicitar la recuperación.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout title="Recupera tu acceso" description="Te enviaremos instrucciones si el correo pertenece a una cuenta activa.">
      {sent ? (
        <div className="auth-success" role="status">
          <Mail aria-hidden="true" />
          <h2>Revisa tu correo</h2>
          <p>Por seguridad, mostramos el mismo resultado exista o no una cuenta con esos datos.</p>
          <Link className="button button--secondary button--full" to="/login">Volver al inicio</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="auth-form">
          {error ? <InlineNotice tone="danger">{error}</InlineNotice> : null}
          <Field label="Correo de trabajo" type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" prefixIcon={Mail} required />
          <Button type="submit" disabled={submitting} className="button--full">{submitting ? "Enviando..." : "Enviar instrucciones"}</Button>
          <Link className="auth-back-link" to="/login"><ArrowLeft size={17} /> Volver al inicio de sesión</Link>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const token = useSensitiveActionToken();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    if (!token && !useDevelopmentFixtures) {
      setError("El enlace de recuperación no es válido.");
      return;
    }
    try {
      if (!useDevelopmentFixtures) await authApi.reset(token, password);
      setMessage("Tu contraseña fue actualizada. Ya puedes iniciar sesión.");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "No fue posible actualizar la contraseña.");
    }
  };

  return (
    <AuthLayout title="Crea una nueva contraseña" description="Usa una frase larga, única y difícil de adivinar.">
      <form onSubmit={submit} className="auth-form">
        {message ? <InlineNotice tone="success">{message}</InlineNotice> : null}
        {error ? <InlineNotice tone="danger">{error}</InlineNotice> : null}
        <PasswordField label="Nueva contraseña" value={password} onChange={setPassword} autoComplete="new-password" />
        <PasswordField label="Confirma la contraseña" value={confirmPassword} onChange={setConfirmPassword} autoComplete="new-password" />
        <Button type="submit" className="button--full">Actualizar contraseña</Button>
        <Link className="auth-back-link" to="/login"><ArrowLeft size={17} /> Volver al inicio</Link>
      </form>
    </AuthLayout>
  );
}

export function InvitationPage() {
  const token = useSensitiveActionToken();
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [working, setWorking] = useState(false);
  const { acceptInvitation } = useAuth();
  const navigate = useNavigate();
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (working) return;
    setError("");
    if (!token && !useDevelopmentFixtures) { setError("La invitación no es válida o ya expiró."); return; }
    setWorking(true);
    try {
      await acceptInvitation(token, name, password);
      navigate("/", { replace: true });
    } catch (caught) { setError(caught instanceof Error ? caught.message : "No fue posible aceptar la invitación."); }
    finally { setWorking(false); }
  };
  return <AuthLayout title="Activa tu cuenta" description="Completa tu perfil y crea tu contraseña para entrar.">
    <form onSubmit={submit} className="auth-form">
      {error ? <InlineNotice tone="danger">{error}</InlineNotice> : null}
      <Field label="Nombre completo" value={name} onChange={(event) => setName(event.target.value)} autoComplete="name" required />
      <PasswordField label="Crea una contraseña" value={password} onChange={setPassword} autoComplete="new-password" />
      <p className="password-rules">Mínimo 12 caracteres. Combina palabras, números y símbolos sin reutilizar otra contraseña.</p>
      <Button type="submit" className="button--full" disabled={working}>{working ? "Activando..." : "Crear cuenta segura"}</Button>
    </form>
  </AuthLayout>;
}
